import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Filter, RotateCcw, Printer, Download as DownloadIcon, Receipt, Percent, Wallet
} from 'lucide-react';
import { toast } from 'react-toastify';
import taxInvoiceService from '../../../services/taxInvoiceService';
import customerMasterService from '../../../services/customerMasterService';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import DataTable from '../../../components/DataTable/DataTable';
import { formatCurrency } from '../../../utils/currencyFormat';
import './TaxInvoiceReport.css';

const TaxInvoiceReport = () => {
  const navigate = useNavigate();
  
  // Get current month start and end dates
  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    // Format dates as YYYY-MM-DD using local timezone
    const formatDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    
    return {
      fromDate: formatDate(startDate),
      toDate: formatDate(endDate)
    };
  };

  const [filters, setFilters] = useState({
    customer: '',
    fromDate: getCurrentMonthDates().fromDate,
    toDate: getCurrentMonthDates().toDate
  });
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);

  // Load customers for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await customerMasterService.getAllCustomers({ limit: 1000 });
        if (response && response.success) {
          setCustomers(response.data || []);
        }
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, []);

  // Load report data
  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await taxInvoiceService.getAllTaxInvoices({
        page: 1,
        limit: 10000, // Get all records for report
        status: 'Approved', // Only show approved invoices
        startDate: filters.fromDate,
        endDate: filters.toDate,
        customer: filters.customer || undefined
      });

      if (response && response.success) {
        const transformedData = (response.data || []).map(invoice => ({
          ...invoice,
          id: invoice._id || invoice.id
        }));
        setInvoices(transformedData);
      } else {
        toast.error('Failed to load tax invoice report');
      }
    } catch (error) {
      console.error('Error loading tax invoice report:', error);
      toast.error('Failed to load tax invoice report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilter = () => {
    loadReportData();
  };

  const handleResetFilter = () => {
    const currentMonth = getCurrentMonthDates();
    setFilters({
      customer: '',
      fromDate: currentMonth.fromDate,
      toDate: currentMonth.toDate
    });
  };

  const handleViewPDF = (invoiceId) => {
    navigate(`/sales/tax-invoice/view/${invoiceId}`);
  };

  const handlePrint = (invoice) => {
    // Open print view in new window
    const printUrl = `/sales/tax-invoice/print/${invoice.id || invoice._id}`;
    const printWindow = window.open(printUrl, '_blank', 'width=1200,height=800');
    if (!printWindow) {
      toast.error('Please allow popups to view the print preview');
    }
  };

  const handleDownload = (invoice) => {
    const invoiceId = invoice?.id || invoice?._id;
    if (!invoiceId) {
      toast.error('This invoice cannot be downloaded');
      return;
    }
    const downloadUrl = `/sales/tax-invoice/print/${invoiceId}?download=1`;
    const downloadWindow = window.open(downloadUrl, '_blank');
    if (!downloadWindow) {
      toast.error('Please allow popups to download the invoice PDF');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatAmount = (amount, currency = 'INR') => formatCurrency(amount || 0, currency || 'INR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Never add USD into an INR total. Each currency is totaled on its own.
  const currencyTotals = useMemo(() => {
    const grouped = invoices.reduce((acc, invoice) => {
      const code = invoice.currency || 'INR';
      if (!acc[code]) {
        acc[code] = {
          currency: code,
          totalInvoiceValue: 0,
          totalGSTAmount: 0,
          totalAmountWithTax: 0
        };
      }
      acc[code].totalInvoiceValue += invoice.totalValue || 0;
      acc[code].totalGSTAmount += invoice.totalTaxAmount || 0;
      acc[code].totalAmountWithTax += invoice.totalAmountWithTax || 0;
      return acc;
    }, {});

    const preferred = ['INR', 'USD', 'EUR'];
    return Object.values(grouped).sort((a, b) => {
      const aIndex = preferred.indexOf(a.currency);
      const bIndex = preferred.indexOf(b.currency);
      return (aIndex === -1 ? preferred.length : aIndex) - (bIndex === -1 ? preferred.length : bIndex);
    });
  }, [invoices]);

  const totalsToShow = currencyTotals.length
    ? currencyTotals
    : [{ currency: 'INR', totalInvoiceValue: 0, totalGSTAmount: 0, totalAmountWithTax: 0 }];

  const summaryCards = [
    { key: 'value', title: 'Invoice value', tone: 'value', icon: Receipt, amount: (row) => row.totalInvoiceValue },
    { key: 'gst', title: 'GST', tone: 'gst', icon: Percent, amount: (row) => row.totalGSTAmount },
    { key: 'total', title: 'Amount with GST', tone: 'total', icon: Wallet, amount: (row) => row.totalAmountWithTax },
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'serviceInvoiceNumber',
      label: 'Invoice #',
      sortable: true
    },
    {
      key: 'serviceInvoiceDate',
      label: 'Invoice Date',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'totalValue',
      label: 'Invoice Value',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'totalTaxAmount',
      label: 'GST Amount',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'totalAmountWithTax',
      label: 'Total Invoice Value',
      sortable: true,
      align: 'right',
      render: (value, row) => formatAmount(value, row?.currency)
    },
    {
      key: 'PONo',
      label: 'PO No.',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true
    },
    {
      key: 'currency',
      label: 'Currency',
      sortable: true,
      align: 'center',
      render: (value) => value || 'INR'
    }
  ];

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Reports',
      path: '/sales?tab=reports',
      onClick: () => navigate('/sales?tab=reports')
    },
    {
      label: 'Tax Invoice Report',
      path: '/sales/reports/tax-invoice',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard tax-invoice-report">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1 style={{ fontSize: '22px' }}>Tax Invoice Report</h1>
          <p style={{ fontSize: '12px' }}>View and analyze approved tax invoices</p>
        </div>
      </div>

      {/* Filters Section - Compact, one row */}
      <div style={{
        background: 'white',
        padding: '16px 20px',
        marginBottom: '20px',
        marginLeft: '0',
        marginRight: '0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderRadius: '0',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto', gap: '10px', alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '4px' }}>Select Customer</label>
            <select
              className="form-input"
              value={filters.customer}
              onChange={(e) => handleFilterChange('customer', e.target.value)}
              style={{ padding: '6px 10px' }}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer._id || customer.id} value={customer._id || customer.id}>
                  {customer.customerName} ({customer.customerCode})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>From Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label required" style={{ marginBottom: '4px' }}>To Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              style={{ padding: '6px 10px' }}
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleApplyFilter}
            style={{ 
              padding: '6px 10px',
              backgroundColor: '#ec4899',
              borderColor: '#ec4899',
              borderRadius: '0',
              minWidth: 'auto'
            }}
            title="Apply Filter"
          >
            <Filter size={16} />
          </button>

          <button
            className="btn-secondary"
            onClick={handleResetFilter}
            style={{ 
              padding: '6px 10px',
              borderRadius: '0',
              minWidth: 'auto'
            }}
            title="Reset Filter"
          >
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="tax-invoice-report-table">
        <DataTable
          data={invoices}
          columns={columns}
          loading={loading}
          noCard={true}
          className="tax-invoice-report-table"
          pageSize={50}
          customActions={[
            {
              type: 'print',
              label: 'Print',
              icon: <Printer size={16} />,
              onClick: handlePrint
            },
            {
              type: 'download',
              label: 'Download',
              icon: <DownloadIcon size={16} />,
              onClick: handleDownload
            }
          ]}
        />
      </div>

      <div className="tax-invoice-kpis" aria-label="Invoice totals by currency">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <article key={card.key} className={`tax-kpi tax-kpi-${card.tone}`}>
              <div className="tax-kpi-head">
                <span className="tax-kpi-icon"><Icon size={16} /></span>
                <span className="tax-kpi-title">{card.title}</span>
              </div>
              <div className="tax-kpi-amounts">
                {totalsToShow.map((row) => (
                  <div key={row.currency} className="tax-kpi-chip">
                    <span className="tax-kpi-code">{row.currency}</span>
                    <span className="tax-kpi-figure">{formatAmount(card.amount(row), row.currency)}</span>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default TaxInvoiceReport;
